import { useEffect, useState } from "react";
import { ExternalLink, Compass } from "lucide-react";
import { api } from "@/lib/api";

export default function SovereignProvisions({ variant = "secondary", kicker = "Island Botanicals", title = "Bath companions we love." }) {
    const [items, setItems] = useState([]);

    useEffect(() => {
        api.get("/satellite-provisions")
            .then(({ data }) => {
                setItems(data.provisions || []);
            })
            .catch(() => setItems([]));
    }, []);

    if (items.length === 0) return null;

    const isHero = variant === "hero";

    return (
        <section
            className={`relative ${isHero ? "bg-cream py-20 md:py-28" : "bg-cream-warm py-20 md:py-24"}`}
            data-testid="sovereign-provisions"
            id="island-botanicals"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <span className="divider-rule text-brown">
                            <Compass size={12} /> {kicker}
                        </span>
                        <h2
                            className={`font-serif text-forest mt-5 leading-[1.02] ${
                                isHero
                                    ? "text-5xl sm:text-6xl lg:text-7xl"
                                    : "text-4xl sm:text-5xl lg:text-6xl"
                            }`}
                        >
                            {isHero ? (
                                <>
                                    Expertly curated
                                    <br />
                                    <span className="italic text-gold">
                                        island rituals.
                                    </span>
                                </>
                            ) : (
                                <>
                                    {title}
                                </>
                            )}
                        </h2>
                        {isHero && (
                            <p className="mt-6 max-w-xl text-base md:text-lg text-brown leading-relaxed">
                                While our first bars get ready, we've hand-picked a few favourite
                                bath companions — hardware, diffusers, and
                                lounge-hour rituals worthy of the Glow Set.
                            </p>
                        )}
                    </div>
                </div>

                <p className="-mt-6 mb-8 text-sm text-brown" data-testid="provisions-disclosure">
                    {items[0].disclosure}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {items.slice(0, 8).map((p, i) => (
                        <a
                            key={p.key}
                            href={p.buy_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block rise-in"
                            style={{ animationDelay: `${i * 80}ms` }}
                            data-testid={`provision-${p.key}`}
                        >
                            <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-cream">
                                <img
                                    src={p.image}
                                    alt={p.name}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                                />
                                <span className="absolute top-3 left-3 text-[0.55rem] tracking-[0.3em] uppercase bg-cream/90 text-forest px-2.5 py-1 rounded-full">
                                    {p.category_label}
                                </span>
                            </div>
                            <div className="mt-3">
                                <h4 className="font-serif text-lg text-forest leading-tight group-hover:text-gold transition-colors">
                                    {p.name}
                                </h4>
                                <p className="text-[0.65rem] tracking-[0.2em] uppercase text-brown mt-1">
                                    {p.vendor}
                                </p>
                                <div className="mt-2 flex items-center justify-between">
                                    <span className="font-serif text-base text-forest">
                                        ${Number(p.price).toFixed(0)}
                                    </span>
                                    <span className="text-[0.6rem] tracking-[0.25em] uppercase text-gold inline-flex items-center gap-1">
                                        Buy now <ExternalLink size={10} />
                                    </span>
                                </div>
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}
